// Lo que ha costado este turno (R4, CA4).
//
// El prototipo **no lo pedía**: la capacidad es más nueva que el diseño. Se
// añadió el 2026-08-25 midiendo que una conversación de seis preguntas costaba
// 0,63 USD y que el sexto turno costaba doce veces el primero.
//
// **Va escondido detrás de un icono, no plegado con su resumen a la vista.**
// La primera version enseñaba «3.085 tokens · 0,64 ¢» debajo de cada respuesta
// y eso convierte una conversacion en un extracto bancario: es un dato de
// operacion, no la respuesta. Quien pregunta algo quiere su respuesta; quien
// vigila la factura pulsa el icono.

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { Gauge } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CosteDelTurno as Coste } from '@/services/brainApi'

// El formato se escribe a mano en vez de usar `toLocaleString('es-ES')`, y no
// es por gusto: el contenedor de node no lleva los datos de ICU para es-ES y
// devuelve «7880» donde un navegador devuelve «7.880». Una cifra de coste que
// cambia de forma segun donde se pinte no se puede comparar ni probar.

/** Miles separados con punto, siempre igual en cualquier entorno. */
function miles(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

function conComa(n: number, decimales: number): string {
  return n.toFixed(decimales).replace('.', ',')
}

/** Céntimos hasta el dólar, dólares a partir de ahí.
 *
 * El corte no es arbitrario: un turno del Brain costó entre 1,6 y 19 céntimos
 * en las medidas del 2026-08-25, así que la unidad natural aquí es el céntimo.
 * «0,017 $» es la misma cifra y se lee mucho peor.
 */
function enDinero(usd: number): string {
  if (usd < 1) return `${conComa(usd * 100, 2)} ¢`
  return `${conComa(usd, 2)} $`
}

export function CosteDelTurno({ coste }: { coste: Coste }) {
  const { t } = useTranslation()
  const [abierto, setAbierto] = useState(false)

  const dinero =
    coste.coste_usd === null
      ? t('brain.sin_tarifa')
      : `${enDinero(coste.coste_usd)}${coste.coste_es_techo ? t('brain.coste_maximo') : ''}`

  return (
    <div className="mt-1.5">
      <Button
        variant="link"
        onClick={() => setAbierto((v) => !v)}
        title={t('brain.coste_resumen', { tokens: miles(coste.tokens_total), dinero })}
        aria-label={t('brain.coste_resumen', { tokens: miles(coste.tokens_total), dinero })}
        className={cn('text-muted-foreground hover:text-foreground', abierto && 'text-foreground')}
      >
        <Gauge className="h-3 w-3" />
      </Button>

      {abierto && (
        <div className="mt-1.5 space-y-2 rounded-md border bg-muted/20 px-2.5 py-2 text-xs">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
            <Dato etiqueta={t('brain.entrada')} valor={miles(coste.tokens_entrada)} />
            <Dato etiqueta={t('brain.salida')} valor={miles(coste.tokens_salida)} />
            <Dato etiqueta={t('brain.llamadas_modelo')} valor={String(coste.llamadas_al_modelo)} />
            <Dato etiqueta={t('brain.herramientas')} valor={String(coste.herramientas_invocadas)} />
          </div>

          {coste.coste_es_techo && (
            // Sin esto, la cifra se lee como exacta y no lo es. Se dice **por
            // que** no lo es, que es lo unico que permite juzgarla.
            <p className="text-muted-foreground">
              {t('brain.coste_es_techo_por_que', { cache: miles(coste.tokens_entrada_de_cache) })}
            </p>
          )}

          {coste.herramientas_con_fallo > 0 && (
            <p className="text-warning">
              {coste.herramientas_con_fallo === 1
                ? t('brain.fallaron_una')
                : t('brain.fallaron_varias', { count: coste.herramientas_con_fallo })}
            </p>
          )}

          {coste.por_modelo.length > 1 && (
            <ul className="space-y-1 border-t border-border pt-1.5">
              {coste.por_modelo.map((m) => (
                <li key={`${m.proveedor}-${m.modelo}`} className="flex gap-2">
                  <span className="font-mono">{m.modelo}</span>
                  <span className="text-muted-foreground">
                    {m.llamadas} · {miles(m.tokens_entrada + m.tokens_salida)} tokens
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <span className="text-muted-foreground">{etiqueta}: </span>
      <span className="font-mono">{valor}</span>
    </div>
  )
}
